import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AttendeeFields } from '@mr-website/schemas';
import { useState } from 'react';
import type { FieldErrors } from './utils';

interface AttendeeFieldsFormProps {
  idPrefix: string;
  values: AttendeeFields;
  errors?: FieldErrors;
  readOnly?: boolean;
  hideNames?: boolean;
  hideEmailField?: boolean;
  emailRequired?: boolean;
  onChange: (next: AttendeeFields) => void;
  onFieldBlur?: (field: keyof AttendeeFields, values: AttendeeFields) => void;
}

export function AttendeeFieldsForm({
  idPrefix,
  values,
  errors = {},
  readOnly = false,
  hideNames = false,
  hideEmailField = false,
  emailRequired = false,
  onChange,
  onFieldBlur,
}: AttendeeFieldsFormProps) {
  const inviteId = `${idPrefix}-send-invite`;
  const emailId = `${idPrefix}-email`;
  const [focusedField, setFocusedField] = useState<keyof AttendeeFields | null>(null);

  function update<K extends keyof AttendeeFields>(key: K, value: AttendeeFields[K]) {
    onChange({ ...values, [key]: value });
  }

  function showFieldError(field: keyof AttendeeFields): boolean {
    return focusedField !== field && !!errors[field];
  }

  function handleTextFieldBlur(field: 'first_name' | 'last_name' | 'email', rawValue: string) {
    setFocusedField(null);
    let nextValues = values;
    const trimmed = rawValue.trim();
    if (field === 'email') {
      const nextEmail = trimmed || undefined;
      if (nextEmail !== values.email) {
        nextValues = { ...values, email: nextEmail };
        update('email', nextEmail);
      }
    } else if (trimmed !== values[field]) {
      nextValues = { ...values, [field]: trimmed };
      update(field, trimmed);
    }
    onFieldBlur?.(field, nextValues);
  }

  function handleInviteChange(checked: boolean) {
    onChange({
      ...values,
      send_invite: checked,
      email: checked || emailRequired ? values.email : undefined,
    });
  }

  return (
    <div className="space-y-3">
      {hideNames ? null : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field data-invalid={showFieldError('first_name') || undefined}>
            <FieldLabel htmlFor={`${idPrefix}-first-name`}>First name</FieldLabel>
            <FieldContent>
              {readOnly ? (
                <p className="text-sm">{values.first_name || '—'}</p>
              ) : (
                <Input
                  id={`${idPrefix}-first-name`}
                  value={values.first_name}
                  autoComplete="given-name"
                  onFocus={() => setFocusedField('first_name')}
                  onBlur={(e) => handleTextFieldBlur('first_name', e.target.value)}
                  onChange={(e) => update('first_name', e.target.value)}
                />
              )}
              {showFieldError('first_name') ? (
                <FieldError errors={[{ message: errors.first_name! }]} />
              ) : null}
            </FieldContent>
          </Field>

          <Field data-invalid={showFieldError('last_name') || undefined}>
            <FieldLabel htmlFor={`${idPrefix}-last-name`}>Last name</FieldLabel>
            <FieldContent>
              {readOnly ? (
                <p className="text-sm">{values.last_name || '—'}</p>
              ) : (
                <Input
                  id={`${idPrefix}-last-name`}
                  value={values.last_name}
                  autoComplete="family-name"
                  onFocus={() => setFocusedField('last_name')}
                  onBlur={(e) => handleTextFieldBlur('last_name', e.target.value)}
                  onChange={(e) => update('last_name', e.target.value)}
                />
              )}
              {showFieldError('last_name') ? (
                <FieldError errors={[{ message: errors.last_name! }]} />
              ) : null}
            </FieldContent>
          </Field>
        </div>
      )}

      <div className="flex items-start gap-2">
        <input
          id={inviteId}
          type="checkbox"
          className="mt-1 size-4 rounded border border-input accent-mr-teal-dark"
          checked={values.send_invite}
          onChange={(e) => handleInviteChange(e.target.checked)}
        />
        <Label htmlFor={inviteId} className="font-normal leading-snug text-foreground">
          Also send them an invite email
        </Label>
      </div>

      {(values.send_invite && !hideEmailField) || emailRequired ? (
        <Field data-invalid={showFieldError('email') || undefined}>
          <FieldLabel htmlFor={emailId}>Email{emailRequired ? ' *' : ''}</FieldLabel>
          <FieldContent>
            {readOnly ? (
              <p className="text-sm">{values.email || '—'}</p>
            ) : (
              <Input
                id={emailId}
                type="email"
                value={values.email ?? ''}
                autoComplete="email"
                required={emailRequired}
                onFocus={() => setFocusedField('email')}
                onBlur={(e) => handleTextFieldBlur('email', e.target.value)}
                onChange={(e) => update('email', e.target.value)}
              />
            )}
            <p className="text-xs text-muted-foreground">
              {emailRequired
                ? values.send_invite
                  ? 'Required to add your spouse. They will receive an email with the event details.'
                  : 'Required to add your spouse to your account.'
                : 'They will receive an email with the event details.'}
            </p>
            {showFieldError('email') ? <FieldError errors={[{ message: errors.email! }]} /> : null}
          </FieldContent>
        </Field>
      ) : null}
    </div>
  );
}
